import React, { useState, useEffect } from 'react';
import { AdminQuery } from '../types';
import { adminQueriesService } from '../services/admin-queries.service';
import { api } from '../../../../core/utils/apiClient';
import { COLORS } from '../../../../core/constants/colors';

interface QueryDetailsDrawerProps {
  queryId: number | null;
  query?: AdminQuery | null;
  isOpen: boolean;
  onClose: () => void;
}

export const QueryDetailsDrawer: React.FC<QueryDetailsDrawerProps> = ({
  queryId,
  query: queryProp,
  isOpen,
  onClose,
}) => {
  const [query, setQuery] = useState<AdminQuery | null>(queryProp || null);
  const [company, setCompany] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (queryProp) {
        setQuery(queryProp);
        if (queryProp.company_id) {
          loadCompany(queryProp.company_id);
        }
      } else if (queryId) {
        loadQuery();
      }
    } else {
      setQuery(null);
      setCompany(null);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, queryId, queryProp]);

  const loadQuery = async () => {
    if (!queryId) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await adminQueriesService.getQueryById(queryId);
      if (data) {
        setQuery(data);
        if (data.company_id) {
          loadCompany(data.company_id);
        }
      } else {
        setError('Query not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load query');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompany = async (companyId: number) => {
    try {
      const response = await api.get(`/api/v1/companies/${companyId}`);
      if (response.ok) {
        const data = await response.json();
        setCompany(data);
      }
    } catch (error) {
      console.error('Error loading company:', error);
    }
  };

  if (!isOpen) return null;

  const answerText = query?.answer || query?.response_text || query?.answer_preview || 'No response available';

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Right Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Query Details
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-t-transparent mb-3"
                    style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                  />
                  <p className="text-sm" style={{ color: '#6B7280' }}>Loading query...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#DC2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
                </div>
              </div>
            )}

            {query && !isLoading && (
              <div className="space-y-6">
                {/* Query Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium mb-1 block" style={{ color: '#6B7280' }}>Query ID</label>
                    <p className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                      #{query.id}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block" style={{ color: '#6B7280' }}>Status</label>
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium inline-block"
                      style={{
                        backgroundColor: query.status === 'success' ? '#D1FAE5' : '#FEE2E2',
                        color: query.status === 'success' ? '#059669' : '#DC2626'
                      }}
                    >
                      {query.status}
                    </span>
                  </div>
                </div>

                {company && (
                  <div>
                    <label className="text-xs font-medium mb-1 block" style={{ color: '#6B7280' }}>Company</label>
                    <p className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                      {company.name}
                    </p>
                  </div>
                )}

                {/* Query Text */}
                <div>
                  <label className="text-xs font-medium mb-2 block" style={{ color: '#6B7280' }}>Query</label>
                  <div className="border rounded-lg p-4 bg-gray-50" style={{ borderColor: '#E5E7EB' }}>
                    <p className="text-sm whitespace-pre-wrap break-words" style={{ color: '#1F2937' }}>
                      {query.query_text}
                    </p>
                  </div>
                </div>

                {/* Answer/Response */}
                <div>
                  <label className="text-xs font-medium mb-2 block" style={{ color: '#6B7280' }}>Answer</label>
                  <div className="border rounded-lg p-4" style={{ borderColor: '#E5E7EB', backgroundColor: '#F0F9FF' }}>
                    <div 
                      className="text-sm whitespace-pre-wrap break-words"
                      style={{ color: '#1F2937' }}
                    >
                      {answerText}
                    </div>
                  </div>
                </div>

                {/* Sources */}
                {query.sources && query.sources.length > 0 && (
                  <div>
                    <label className="text-xs font-medium mb-2 block" style={{ color: '#6B7280' }}>
                      Sources ({query.sources.length})
                    </label>
                    <div className="border rounded-lg p-4 max-h-64 overflow-y-auto" style={{ borderColor: '#E5E7EB' }}>
                      <div className="space-y-3">
                        {query.sources.map((source: any, index: number) => (
                          <div key={index} className="p-3 rounded-lg bg-gray-50">
                            {source.document_name && (
                              <p className="text-xs font-medium mb-1" style={{ color: '#1F2937' }}>
                                {source.document_name}
                              </p>
                            )}
                            {source.chunk_text && (
                              <p className="text-xs line-clamp-3" style={{ color: '#6B7280' }}>
                                {source.chunk_text}
                              </p>
                            )}
                            {source.relevance_score !== undefined && (
                              <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                                Relevance: {(source.relevance_score * 100).toFixed(1)}%
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                  <div>
                    <label className="text-xs font-medium mb-1 block" style={{ color: '#6B7280' }}>Created At</label>
                    <p className="text-sm" style={{ color: '#1F2937' }}>
                      {new Date(query.created_at).toLocaleString()}
                    </p>
                  </div>
                  {query.response_time_ms && (
                    <div>
                      <label className="text-xs font-medium mb-1 block" style={{ color: '#6B7280' }}>Response Time</label>
                      <p className="text-sm" style={{ color: '#1F2937' }}>
                        {Math.round(query.response_time_ms)}ms
                      </p>
                    </div>
                  )}
                </div>

                {query.model_used && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium mb-1 block" style={{ color: '#6B7280' }}>Model Used</label>
                      <p className="text-sm" style={{ color: '#1F2937' }}>{query.model_used}</p>
                    </div>
                    {query.tokens_used && (
                      <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: '#6B7280' }}>Tokens Used</label>
                        <p className="text-sm" style={{ color: '#1F2937' }}>{query.tokens_used.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
